import {
  AlertTriangle,
  CheckCircle2,
  RotateCw,
  ScanText,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { cn } from "../../../lib/utils";

/**
 * The per-file cards on the claim upload pages: one while the document is
 * being uploaded and read by Vista's AI extraction, one for the result.
 *
 * They are the same object in two states, not two designs. Both are the same
 * row — document plate, status chip, headline, filename — at the same size, so
 * the swap at the end of the read changes what the card says without moving
 * anything. That is the whole reason the plate, the shell and the tone table
 * live in one file: if the two ever drift apart, the transition breaks.
 *
 * The plate replaced a centred spinner and a centred tick. A spinner says
 * "something is happening somewhere"; for a wait that can run a full minute on
 * a page where the customer has just handed over their driving licence, they
 * need to see that the thing being worked on is *their* document — and then
 * that the same document is the one that passed.
 */

// Per-state palette. Deliberately softer than the full-saturation #00A9E7 and
// #2E9E5B outlines these cards used to carry: the plate is the only thing in
// the row that should be pulling the eye.
const TONES = {
  reading: {
    card: "border-[#CDEAF9] bg-[#F4FAFF]",
    chip: "bg-[#00A9E7]/10 text-[#076A96]",
    bracket: "border-[#4FD1FF]/85",
    bar: "bg-[#4FD1FF]",
  },
  verified: {
    card: "border-[#BEE3CD] bg-[#F4FCF7]",
    chip: "bg-[#2E9E5B]/12 text-[#1B6B3D]",
    bracket: "border-[#57D391]/85",
    bar: "bg-[#57D391]",
  },
  neutral: {
    card: "border-[#DDE2E4] bg-[#F7F9FA]",
    chip: "bg-[#5B6871]/10 text-[#48535B]",
    bracket: "border-white/45",
    bar: "bg-white/40",
  },
  error: {
    card: "border-[#F2C3BF] bg-[#FEF6F5]",
    chip: "bg-[#D93F35]/10 text-[#9E251D]",
    bracket: "border-[#FF9084]/85",
    bar: "bg-[#FF9084]",
  },
};

// PDFs, restored files with no readable source, and any image the browser will
// not decode fall back to a drawn page. The bars are geometry, not extracted
// text — they are identical in every layer, so the sweep recolours them rather
// than filling anything in.
const PAGE_BARS = [100, 84, 92, 68, 96, 78, 46];

const CORNER_CLASSES = [
  "top-[5px] start-[5px] border-t-[1.5px] border-s-[1.5px] rounded-ss-[3px]",
  "top-[5px] end-[5px] border-t-[1.5px] border-e-[1.5px] rounded-se-[3px]",
  "bottom-[5px] start-[5px] border-b-[1.5px] border-s-[1.5px] rounded-es-[3px]",
  "bottom-[5px] end-[5px] border-b-[1.5px] border-e-[1.5px] rounded-ee-[3px]",
];

// The minute the reading copy promises. Past it, silence reads as a hang.
const LONG_WAIT_MS = 60000;

/**
 * A restored file has been through redux and is no longer a Blob, so its
 * `type` may be gone; the extension is the only thing left to go on.
 */
const isImageFile = (file) => {
  if (typeof file?.type === "string" && file.type) {
    return file.type.startsWith("image/");
  }
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(file?.name || "");
};

/** The uploaded copy, only ever from our own files API. */
const remotePreviewOf = (file) =>
  typeof file?.preview === "string" &&
  file.preview.startsWith("https://files-api.solidarity.bh")
    ? file.preview
    : null;

const PlateLayer = ({ src, barClass, dimmed }) => {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn(
          "w-full h-full object-cover",
          dimmed
            ? "grayscale-[0.75] brightness-[0.85] contrast-[1.1] opacity-[0.42]"
            : "saturate-[1.06] contrast-[1.04]"
        )}
      />
    );
  }
  return (
    <div className="absolute inset-0 flex flex-col justify-center gap-[5px] px-[10px]">
      {PAGE_BARS.map((width, index) => (
        <span
          key={index}
          style={{ width: `${width}%` }}
          className={cn(
            "h-[4px] rounded-full",
            dimmed ? "bg-white/20" : barClass
          )}
        />
      ))}
    </div>
  );
};

/**
 * The document itself, on a dark sensor surface.
 *
 * `scan` runs the reading head over it; every other state is a still frame of
 * the same plate, which is what lets the card change state without the
 * document appearing to move or reload.
 */
const DocumentPlate = ({ file, tone, scan = null, dimmed = false }) => {
  // Our own object URL rather than `file.preview`: the flow swaps preview from
  // the local blob to the remote files-api link the moment the upload lands,
  // and re-pointing the plate mid-scan would blank it for a network round
  // trip. Revoked on unmount, which the flow's own previews never are.
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (!isImageFile(file) || typeof Blob === "undefined") return undefined;
    if (!(file instanceof Blob)) return undefined;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => {
      setObjectUrl(null);
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Files restored from redux on Back are no longer Blobs — their uploaded
  // copy is the only source left, so the plate still shows the real document
  // instead of dropping to the drawn page.
  const src = isImageFile(file) ? objectUrl || remotePreviewOf(file) : null;
  const palette = TONES[tone];

  return (
    <div
      aria-hidden="true"
      className={cn(
        // `isolate` bounds the reading head's mix-blend-mode to this plate.
        // Without it the blend group is the nearest ancestor stacking context —
        // the card, or higher — so every frame of a 2.4s infinite animation
        // repaints all of it, per file. Chrome hides the cost on the GPU;
        // Firefox composites blended layers on the main thread and flickers.
        // Nothing looks different: the beam only ever overlaps the plate.
        "relative isolate shrink-0 overflow-hidden rounded-[8px] bg-[#0B2F42]",
        "w-[88px] h-[104px] max-390:w-[72px] max-390:h-[86px]",
        "ring-1 ring-inset ring-white/10",
        // A viewfinder vignette plus an unmoving pool of brand light: the
        // inset darkening keeps the corner brackets legible over a bright
        // document, and gives the reading head somewhere to happen without
        // adding a second thing in motion.
        "shadow-[inset_0_12px_26px_-14px_rgba(0,0,0,0.75),inset_0_-12px_26px_-14px_rgba(0,0,0,0.75),0_0_0_4px_rgba(0,169,231,0.06),0_8px_18px_-10px_rgba(0,86,122,0.45)]"
      )}
    >
      <PlateLayer src={src} barClass={palette.bar} dimmed={dimmed || !!scan} />

      {scan && (
        <div
          data-scan-read
          className={cn(
            "absolute inset-0",
            scan === "analyzing" ? "animate-scanReveal" : "animate-scanBreathe"
          )}
        >
          <PlateLayer src={src} barClass={palette.bar} />
        </div>
      )}

      {scan === "analyzing" && (
        <div className="absolute inset-0 overflow-hidden mix-blend-screen">
          <div
            data-scan-beam
            className={cn(
              "absolute inset-x-0 top-0 h-[45%] animate-scanSweep",
              "bg-[linear-gradient(to_bottom,transparent_0%,rgba(0,169,231,0.22)_45%,rgba(79,209,255,0.55)_82%,rgba(168,238,255,0.9)_97%,rgba(255,255,255,1)_100%)]"
            )}
          >
            <span className="absolute bottom-0 inset-x-0 h-[1.5px] bg-white shadow-[0_0_10px_2px_rgba(0,169,231,0.95)]" />
          </div>
        </div>
      )}

      {CORNER_CLASSES.map((corner) => (
        <span
          key={corner}
          className={cn("absolute w-[9px] h-[9px]", palette.bracket, corner)}
        />
      ))}
    </div>
  );
};

/** The row both cards are: one plate, one text column, identical geometry. */
const DocumentCardShell = ({
  tone,
  entrance = false,
  plate,
  actions,
  // Sits over the card's top corner rather than in the row, so the two states
  // stay the same geometry and the swap at the end of a read still moves
  // nothing.
  corner,
  children,
}) => (
  <Card
    data-scan-card={entrance ? "" : undefined}
    className={cn(
      "relative w-full mb-3 flex items-center gap-4 px-4 py-4",
      "rounded-[10px] shadow-[0_1px_2px_rgba(11,47,66,0.05)]",
      TONES[tone].card,
      entrance && "animate-scanCardIn"
    )}
  >
    {plate}
    <div className={cn("min-w-0 flex-1 text-start", corner && "pe-7")}>
      {/* Only the verdict is live. The actions sit outside it so a screen
          reader announces what happened to the document, not its buttons. */}
      <div role="status" aria-live="polite">
        {children}
      </div>
      {actions}
    </div>
    {corner}
  </Card>
);

const StatusChip = ({ tone, icon, children }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-[3px]",
      "text-[10px] font-semibold leading-none",
      // Arabic letters join — tracking them apart breaks the word, so the
      // label styling is latin-only.
      "ltr:uppercase ltr:tracking-[0.08em]",
      TONES[tone].chip
    )}
  >
    {icon}
    {children}
  </span>
);

/** An outlined chip for a fact about the document, not its state. */
const DetailChip = ({ children }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full border border-[#CFDBE1] bg-white/70 px-2 py-[3px]",
      "text-[10px] font-semibold leading-none text-[#48535B]",
      "ltr:uppercase ltr:tracking-[0.08em]"
    )}
  >
    {children}
  </span>
);

const CardAction = ({ onClick, icon, primary = false, children }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    className={cn(
      "h-7 -ms-2 gap-1.5 rounded-[6px] px-2 text-xs font-semibold",
      "transition-[background-color,color,transform] duration-150 ease-out",
      "active:scale-[0.97]",
      primary
        ? "text-[#076A96] hover:bg-[#00A9E7]/10 hover:text-[#055B80]"
        : "text-[#48535B] hover:bg-black/[0.05] hover:text-[#252C32]"
    )}
  >
    {icon}
    {children}
  </Button>
);

/**
 * The way out of a wait. A read can run a full minute, and a customer who
 * picked the wrong photo should not have to watch it finish before they can
 * take it back.
 */
const CancelButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={cn(
      "absolute top-2 end-2 inline-flex h-7 w-7 items-center justify-center",
      "rounded-full text-[#5B6871]",
      "transition-[background-color,color,transform] duration-150 ease-out",
      "hover:bg-black/[0.06] hover:text-[#252C32] active:scale-[0.94]"
    )}
  >
    <X className="h-4 w-4" aria-hidden="true" />
  </button>
);

export const DocumentScanCard = ({ file, mode, onCancel }) => {
  const translate = useLanguageTranslate();
  const analyzing = mode === "analyzing";

  // One timeout, not a ticking clock. A live elapsed counter only gives
  // someone who is already waiting something to watch; the single fact worth
  // saying is that this one is running long.
  const [longWait, setLongWait] = useState(false);
  useEffect(() => {
    if (!analyzing) return undefined;
    const timer = setTimeout(() => setLongWait(true), LONG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [analyzing]);

  return (
    <DocumentCardShell
      tone="reading"
      entrance
      plate={<DocumentPlate file={file} tone="reading" scan={mode} />}
      corner={
        onCancel ? (
          <CancelButton
            onClick={onCancel}
            label={
              analyzing
                ? translate("Cancel this check", "إلغاء هذا الفحص")
                : translate("Cancel this upload", "إلغاء هذا التحميل")
            }
          />
        ) : null
      }
    >
      {analyzing && (
        <StatusChip
          tone="reading"
          icon={<ScanText className="w-3 h-3" aria-hidden="true" />}
        >
          {translate("AI", "الذكاء الاصطناعي")}
        </StatusChip>
      )}
      <p
        className={cn("text-[#252C32] text-sm font-semibold", analyzing && "mt-2")}
      >
        {analyzing
          ? translate("Reading your document…", "جارٍ قراءة مستندك…")
          : translate("Uploading your document…", "جارٍ تحميل مستندك…")}
      </p>
      {analyzing && (
        <p className="text-[#5B6871] text-xs mt-1">
          {longWait
            ? translate(
                "Still reading — this one is taking longer than usual.",
                "لا تزال القراءة جارية — يستغرق هذا المستند وقتًا أطول من المعتاد."
              )
            : translate(
                "This can take up to a minute",
                "قد يستغرق ذلك ما يصل إلى دقيقة"
              )}
        </p>
      )}
      <p className="text-[#5B6871] text-xs mt-1 truncate" title={file?.name}>
        {file?.name}
      </p>
    </DocumentCardShell>
  );
};

const SIDE_LABELS = {
  front: ["Front side", "الوجه الأمامي"],
  back: ["Back side", "الوجه الخلفي"],
  both: ["Both sides", "الوجهان الأمامي والخلفي"],
};

/**
 * The settled card: what the read concluded.
 *
 * `justRead` is true only when this file was watched through its scan on this
 * mount. Restoring verified files from redux on Back must not replay the tick —
 * two ticks popping on arrival is a page announcing itself, not feedback for
 * something that just happened.
 */
export const DocumentResultCard = ({
  file,
  state,
  justRead = false,
  onRemove,
  onRetry,
}) => {
  const translate = useLanguageTranslate();

  const failed = [
    "scan_failed",
    "wrong_document",
    "unreadable",
    "upload_failed",
  ].includes(state);
  const verified = state === "verified";
  const tone = failed ? "error" : verified ? "verified" : "neutral";

  const failureMessage =
    state === "scan_failed"
      ? translate(
          "This document failed our security scan. Please re-capture and upload it again.",
          "لم يجتز هذا المستند الفحص الأمني. يرجى إعادة التقاطه وتحميله مرة أخرى."
        )
      : state === "wrong_document"
      ? translate(
          "This doesn't look like the right document for this step. Please upload the correct document.",
          "لا يبدو أن هذا هو المستند الصحيح لهذه الخطوة. يرجى تحميل المستند الصحيح."
        )
      : state === "unreadable"
      ? translate(
          "We couldn't read this document. Please check it's the right document and clearly photographed, then upload it again.",
          "لم نتمكن من قراءة هذا المستند. يرجى التأكد من أنه المستند الصحيح وأن الصورة واضحة، ثم إعادة تحميله."
        )
      : translate(
          "This file was not uploaded correctly. Please remove and try again.",
          "لم يتم تحميل هذا الملف بشكل صحيح. يرجى إزالته والمحاولة مرة أخرى."
        );

  // Truthful side label from Vista's content-based detection — this replaced
  // the upload-order "1/2" guess, and it is the fact that tells the customer
  // whether they still owe us the other side, so it sits beside the verdict.
  const sideLabel = verified ? SIDE_LABELS[file?._vistaSide] : null;

  return (
    <DocumentCardShell
      tone={tone}
      plate={<DocumentPlate file={file} tone={tone} dimmed={failed} />}
      actions={
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {state === "upload_failed" && (
            <CardAction
              primary
              onClick={onRetry}
              icon={<RotateCw className="w-3.5 h-3.5" aria-hidden="true" />}
            >
              {translate("Retry", "إعادة المحاولة")}
            </CardAction>
          )}
          <CardAction
            onClick={onRemove}
            icon={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
          >
            {translate("Remove", "إزالة")}
          </CardAction>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {failed ? (
          <StatusChip
            tone="error"
            icon={<AlertTriangle className="w-3 h-3" aria-hidden="true" />}
          >
            {state === "upload_failed"
              ? translate("Not uploaded", "لم يتم التحميل")
              : translate("Not accepted", "غير مقبول")}
          </StatusChip>
        ) : verified ? (
          <StatusChip
            tone="verified"
            icon={
              <CheckCircle2
                data-scan-tick={justRead ? "" : undefined}
                className={cn("w-3 h-3", justRead && "animate-tickPop")}
                aria-hidden="true"
              />
            }
          >
            {translate("Verified", "تم التحقق")}
          </StatusChip>
        ) : (
          <StatusChip tone="neutral">
            {translate("Uploaded", "تم التحميل")}
          </StatusChip>
        )}
        {sideLabel && (
          <DetailChip>{translate(sideLabel[0], sideLabel[1])}</DetailChip>
        )}
      </div>

      {(failed || verified) && (
        <p
          className={cn(
            "mt-2 text-sm font-semibold",
            failed ? "text-[#9E251D]" : "text-[#252C32]"
          )}
        >
          {failed
            ? failureMessage
            : translate(
                "Document verified successfully",
                "تم التحقق من المستند بنجاح"
              )}
        </p>
      )}

      <p className="text-[#5B6871] text-xs mt-1 truncate" title={file?.name}>
        {file?.name}
      </p>
    </DocumentCardShell>
  );
};
